import { LayaGL } from "../../../layagl/LayaGL";
import { Color } from "../../../maths/Color";
import { Vector2 } from "../../../maths/Vector2";
import { Vector4 } from "../../../maths/Vector4";
import { checkShaderDataValueLegal } from "../../../RenderDriver/DriverDesign/RenderDevice/ShaderData";
import { Texture2D } from "../../../resource/Texture2D";
import { ShaderDefines2D } from "../../../webgl/shader/d2/ShaderDefines2D";
import { RenderState2D } from "../../../webgl/utils/RenderState2D";
import { Render2DProcessor } from "../../Render2DProcessor";
import { Mesh2DRender } from "../Mesh2DRender";
import { Blit2DCMD } from "./Blit2DCMD";
import { DrawMesh2DCMD } from "./DrawMesh2DCMD";
import { DrawRenderElement2DCMD } from "./DrawRenderElement2DCMD";
import { Set2DRTCMD } from "./Set2DRenderTargetCMD";
import { Set2DDefineCMD, Set2DShaderDataCMD } from "./Set2DShaderDataCMD";
export class CommandBuffer2D {
    constructor(name) {
        this._scene = null;
        this._renderCMDs = [];
        this.cacheData = {};
        this._cacheViewport = new Vector4();
        this._name = name;
        this.shaderData = LayaGL.renderDeviceFactory.createShaderData();
        this._context = Render2DProcessor.rendercontext2D;
        this._commands = [];
    }
    getName() {
        return this._name;
    }
    _cacheContextState() {
        this.cacheData.rt = this._context.getRenderTarget();
        this.cacheData.pipeline = this._context.pipelineMode;
        this.cacheData.invertY = this._context.invertY;
        this._context.getOffscreenView(this._cacheViewport);
    }
    _recoverContextState() {
        let vp = this._cacheViewport;
        this._context.setOffscreenView(vp.z, vp.w, vp.x, vp.y);
        this._context.setRenderTarget(this.cacheData.rt, false, Color.BLACK);
        this._context.pipelineMode = this.cacheData.pipeline;
        this._context.invertY = this.cacheData.invertY;
    }
    apply(render = true, recoverContextStat = true) {
        this.shaderData.clearData();
        let lastPass = this._context.passData;
        if (lastPass && lastPass.getDefineData()) {
            lastPass.cloneTo(this.shaderData);
        }
        else {
            this.shaderData.setVector2(ShaderDefines2D.UNIFORM_SIZE, Vector2.TEMP.setValue(RenderState2D.width, RenderState2D.height));
        }
        this._context.passData = this.shaderData;
        recoverContextStat && this._cacheContextState();
        for (var i = 0, n = this._commands.length; i < n; i++) {
            let cmd = this._commands[i];
            cmd.run && cmd.run();
        }
        render && this._context.runCMDList(this._renderCMDs);
        this._context.passData = lastPass;
        recoverContextStat && this._recoverContextState();
    }
    applyOne(recoverContextStat = true) {
        recoverContextStat && this._cacheContextState();
        if (this._commands.length) {
            var cmd = this._commands.shift();
            cmd.run && cmd.run();
            cmd.getRenderCMD && this._context.runOneCMD(this._renderCMDs.shift());
            cmd.recover();
        }
        recoverContextStat && this._recoverContextState();
        return this._commands.length > 0;
    }
    clear(recover = true) {
        if (recover)
            for (var i = 0, n = this._commands.length; i < n; i++)
                this._commands[i].recover();
        this._commands.length = 0;
        this._renderCMDs.length = 0;
    }
    getCommandsSize() {
        return this._commands.length;
    }
    setShaderDataValue(shaderData, nameID, dataType, value) {
        if (!checkShaderDataValueLegal(value, dataType))
            return;
        let cmd = Set2DShaderDataCMD.create(shaderData, nameID, value, dataType);
        cmd._globalMode = false;
        this._commands.push(cmd);
        cmd._commandBuffer = this;
        cmd.getRenderCMD && this._renderCMDs.push(cmd.getRenderCMD());
    }
    setGlobalShaderDataValue(nameID, dataType, value) {
        if (!checkShaderDataValueLegal(value, dataType))
            return;
        if (!this._scene || (!this._scene._specialManager)) {
            return;
        }
        let cmd = Set2DShaderDataCMD.create(this._scene._specialManager._shaderData, nameID, value, dataType);
        cmd._globalMode = true;
        this._commands.push(cmd);
        cmd._commandBuffer = this;
        cmd.getRenderCMD && this._renderCMDs.push(cmd.getRenderCMD());
    }
    setShaderDefine(shaderData, define, value) {
        let cmd = Set2DDefineCMD.create(shaderData, define, value);
        cmd._globalMode = false;
        this._commands.push(cmd);
        cmd._commandBuffer = this;
        cmd.getRenderCMD && this._renderCMDs.push(cmd.getRenderCMD());
    }
    blitTextureQuad(source, dest, offsetScale, shader, shaderData, invert) {
        let cmd = Blit2DCMD.create(source, dest, offsetScale, shader, shaderData, invert);
        this._commands.push(cmd);
        cmd._commandBuffer = this;
        cmd.getRenderCMD && this._renderCMDs.push(cmd.getRenderCMD());
    }
    blitTextureBlur(source, dest, blurParams) {
    }
    setRenderTarget(renderTexture, clearColor, colorValue = Color.BLACK, invertY = true, viewportX = 0, viewportY = 0) {
        let cmd = Set2DRTCMD.create(renderTexture, clearColor, colorValue, invertY, viewportX, viewportY);
        this._commands.push(cmd);
        cmd._commandBuffer = this;
        cmd.getRenderCMD && this._renderCMDs.push(cmd.getRenderCMD());
    }
    drawRenderElement(renderelement, mat) {
        let cmd = DrawRenderElement2DCMD.create(renderelement, mat);
        this._commands.push(cmd);
        cmd._commandBuffer = this;
        cmd.getRenderCMD && this._renderCMDs.push(cmd.getRenderCMD());
    }
    drawMesh(mesh, mat, meshTexture, color, material) {
        let cmd = DrawMesh2DCMD.create(mesh, mat, meshTexture || Texture2D.whiteTexture, color || Color.WHITE, material || Mesh2DRender.mesh2DDefaultMaterial);
        this._commands.push(cmd);
        cmd._commandBuffer = this;
        cmd.getRenderCMD && this._renderCMDs.push(cmd.getRenderCMD());
    }
    drawQuad() {
    }
    drawLine() {
    }
    addCacheCommand(cmd) {
        if (this._scene) {
            if (cmd instanceof Set2DShaderDataCMD) {
                if (cmd._commandBuffer)
                    cmd.setDest(this._scene._specialManager._shaderData);
            }
            if (cmd instanceof Set2DDefineCMD) {
                if (cmd._commandBuffer)
                    cmd.setDest(this._scene._specialManager._shaderData);
            }
        }
        this._commands.push(cmd);
        cmd._commandBuffer = this;
        cmd.getRenderCMD && this._renderCMDs.push(cmd.getRenderCMD());
    }
}
